import { getMetricFilterConfig } from "@/metrics/metric-display"
import type { MetricDirectoryEntry } from "@/metrics/registry"
import type { MetricRecord } from "@/metrics/runtime"

import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import type { ConversationsMetricOutputType } from "../types"

export interface MetricFilterOption {
  id: string
  name: string
  searchText: string
  outputType: ConversationsMetricOutputType
  hasValues: boolean
  unit?: string
  valueOptions?: { value: string; label: string }[]
}

/** Rows influence ordering only; choices always come from the workspace catalog. */
export function buildMetricFilterOptions(
  metrics: Record<string, MetricRecord>,
  conversations: Pick<ConversationsTableResult, "metrics">[],
  directory: Record<string, MetricDirectoryEntry> = {},
): MetricFilterOption[] {
  const withValues = new Set<string>()
  for (const row of conversations) {
    for (const [id, value] of Object.entries(row.metrics)) {
      if (
        typeof value === "string" ||
        (typeof value === "number" && Number.isFinite(value))
      ) {
        withValues.add(id)
      }
    }
  }
  return Object.values(metrics)
    .filter((metric) => metric.id)
    .map((metric): MetricFilterOption => {
      const entry = directory[metric.metric_manager]
      const config = getMetricFilterConfig(metric, entry)
      return {
        id: metric.id,
        name: metric.metric_display_name || metric.metric_name,
        searchText:
          `${metric.metric_display_name} ${metric.metric_name}`.toLowerCase(),
        outputType:
          config.outputType === "float" || config.outputType === "set"
            ? config.outputType
            : "string",
        unit: config.unit,
        valueOptions: config.valueOptions,
        hasValues: withValues.has(metric.id),
      }
    })
    .sort(
      (a, b) =>
        Number(b.hasValues) - Number(a.hasValues) ||
        a.name.localeCompare(b.name),
    )
}
